import {
  connectorAssetsApi,
  connectorConnectApi,
  type BackendConnectorCatalogSchema,
  type ConnectorConnectRequestPayload,
} from "../api";

export interface CreateConnectionInput {
  catalogKey: string;
  credentials: Record<string, unknown>;
  selectedTables?: string[];
  connectorSchema?: BackendConnectorCatalogSchema | null;
  shouldPatchSelectedAssets?: boolean;
}

export interface CreateConnectionResult {
  catalogKey: string;
  connectionId: string;
}

export interface TestConnectionResult {
  ok: boolean;
  message: string;
  errorCode?: string;
  durationMs?: number;
  /** Present when the server persisted a connection row (same `/connect` call). */
  connectionId?: string;
}

const inFlightTestRequests = new Map<string, Promise<TestConnectionResult>>();

const RESERVED_CONNECT_KEYS = new Set([
  "connection_name",
  "auth_type",
  "environment",
  "connection_mode",
  "server_url",
  "selectedTables",
  "tablesByKey",
]);

const collectSelectedTableAssetIds = (
  credentials: Record<string, unknown>,
  selectedTables: string[],
): string[] => {
  const tablesByKey = credentials.tablesByKey;
  if (!tablesByKey || typeof tablesByKey !== "object" || Array.isArray(tablesByKey)) {
    return [];
  }
  const map = tablesByKey as Record<string, Record<string, unknown>>;
  const ids: string[] = [];
  for (const key of selectedTables) {
    const meta = map[key];
    const raw = meta?.tableAssetId;
    if (typeof raw === "string" && raw.trim().length > 0) {
      ids.push(raw.trim());
    }
  }
  return ids;
};

export const patchSelectedAssets = async (
  catalogKey: string,
  credentials: Record<string, unknown>,
  selectedTables: string[],
  shouldPatchSelectedAssets: boolean,
): Promise<void> => {
  if (!shouldPatchSelectedAssets) return;

  const isImportAll = selectedTables.length === 0;

  let tablesToSelect = selectedTables;
  if (isImportAll) {
    const tablesByKey = credentials.tablesByKey;
    if (!tablesByKey || typeof tablesByKey !== "object" || Array.isArray(tablesByKey)) return;
    tablesToSelect = Object.keys(tablesByKey as Record<string, unknown>);
    if (tablesToSelect.length === 0) return;
  }

  const assetIds = collectSelectedTableAssetIds(credentials, tablesToSelect);
  if (!isImportAll && assetIds.length !== tablesToSelect.length) {
    throw new Error(
      "Could not resolve imported tables. Go back to Select tables, wait for the list to load, then try Import again.",
    );
  }

  if (assetIds.length === 0) return;

  try {
    await connectorAssetsApi.bulkSelect(catalogKey, assetIds);
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : "Failed to update table selection on the server.",
    );
  }
};

const buildConnectorConnectRequest = (
  input: CreateConnectionInput,
): ConnectorConnectRequestPayload => {
  const authSchemaProperties =
    (input.connectorSchema?.auth_schema?.properties as
      | Record<string, unknown>
      | undefined) ?? {};
  const configSchemaProperties =
    (input.connectorSchema?.config_schema?.properties as
      | Record<string, unknown>
      | undefined) ?? {};

  const authKeys = new Set(Object.keys(authSchemaProperties));
  const configKeys = new Set(Object.keys(configSchemaProperties));

  const credentialsJson: Record<string, unknown> = {};
  const configJson: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input.credentials)) {
    if (authKeys.has(key)) {
      credentialsJson[key] = value;
      continue;
    }
    if (configKeys.has(key)) {
      configJson[key] = value;
      continue;
    }
    if (!RESERVED_CONNECT_KEYS.has(key)) {
      credentialsJson[key] = value;
    }
  }

  if (Array.isArray(input.selectedTables) && input.selectedTables.length > 0) {
    configJson.selected_tables = input.selectedTables;
  }

  const connectionNameRaw = input.credentials.connection_name;
  const connectionName =
    typeof connectionNameRaw === "string" && connectionNameRaw.trim().length > 0
      ? connectionNameRaw.trim()
      : `${input.catalogKey} connection`;

  const authTypeRaw = input.credentials.auth_type;
  const authType =
    typeof authTypeRaw === "string" && authTypeRaw.trim().length > 0
      ? authTypeRaw
      : (input.connectorSchema?.supported_auth_types[0]?.value ?? "user_pass");

  const connectionModeRaw = input.credentials.connection_mode;
  const supportedConnectionModes = input.connectorSchema?.supported_connection_modes ?? [];
  const fallbackConnectionMode = supportedConnectionModes.includes("live_sync")
    ? "live_sync"
    : (supportedConnectionModes[0] ?? "live_sync");
  const connectionMode =
    typeof connectionModeRaw === "string" && connectionModeRaw.trim().length > 0
      ? connectionModeRaw
      : fallbackConnectionMode;

  const environmentRaw = input.credentials.environment;
  const environment =
    typeof environmentRaw === "string" && environmentRaw.trim().length > 0
      ? environmentRaw
      : "production";

  const serverUrlCandidate =
    (typeof configJson.server_url === "string" && configJson.server_url) ||
    (typeof configJson.account === "string" && configJson.account) ||
    (typeof configJson.accountUrl === "string" && configJson.accountUrl) ||
    (typeof input.credentials.server_url === "string" &&
      input.credentials.server_url) ||
    undefined;

  return {
    connection_name: connectionName,
    auth_type: authType,
    environment,
    connection_mode: connectionMode,
    server_url: serverUrlCandidate,
    config_json: configJson,
    credentials_json: credentialsJson,
  };
};

const isExplicitSuccess = (value: unknown): value is true => value === true;

const buildTestRequestKey = (
  catalogKey: string,
  request: ConnectorConnectRequestPayload,
): string => JSON.stringify([catalogKey, request]);

export const connectorConnectService = {
  test: async (
    catalogKey: string,
    payload: Record<string, unknown>,
    connectorSchema?: BackendConnectorCatalogSchema | null,
  ): Promise<TestConnectionResult> => {
    const request = buildConnectorConnectRequest({
      catalogKey,
      credentials: payload,
      connectorSchema,
    });
    const requestKey = buildTestRequestKey(catalogKey, request);
    const existingRequest = inFlightTestRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const testRequest = (async () => {
      const response = await connectorConnectApi.connect(catalogKey, request);
      const didSucceed = isExplicitSuccess(response.test_result?.success);
      const connectionId =
        didSucceed && typeof response.connection?.id === "string"
          ? response.connection.id
          : undefined;
      return {
        ok: didSucceed,
        message:
          response.test_result?.message ??
          (didSucceed ? "Connection successful" : "Connection test failed"),
        errorCode:
          typeof response.test_result?.details?.error_code === "string"
            ? response.test_result.details.error_code
            : undefined,
        connectionId,
      };
    })();

    inFlightTestRequests.set(requestKey, testRequest);
    try {
      return await testRequest;
    } finally {
      inFlightTestRequests.delete(requestKey);
    }
  },

  create: async (
    input: CreateConnectionInput,
  ): Promise<CreateConnectionResult> => {
    const existingConnectionIdRaw = input.credentials.connectionId;
    const existingConnectionId =
      typeof existingConnectionIdRaw === "string" &&
      existingConnectionIdRaw.trim().length > 0
        ? existingConnectionIdRaw.trim()
        : null;
    if (!existingConnectionId) {
      throw new Error(
        "Connection is not ready yet. Please run Test connection again.",
      );
    }

    const selectedTables = input.selectedTables ?? [];
    await patchSelectedAssets(
      input.catalogKey,
      input.credentials,
      selectedTables,
      Boolean(input.shouldPatchSelectedAssets),
    );

    return {
      catalogKey: input.catalogKey,
      connectionId: existingConnectionId,
    };
  },
};
